import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronDown, ChevronUp, Loader2, Phone } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { chatbotService, type ChatSessionSummaryDto } from "@/services/chatbotService";
import { toast } from "sonner";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";

interface CallRecord {
  id: string;
  caller: string;
  phone: string;
  timestamp: string;
  summary: string;
  sentiment: string;
  duration: string;
  transcript: string;
  fullSummary: string;
  reason: string;
  actions: string[];
  messages?: Array<{ role: string; content: string; timestamp?: string; order?: number; source?: string, isVoice?: boolean }>;
}

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const parseActions = (action?: string): string[] => {
  if (!action) return [];
  try {
    const parsed = JSON.parse(action);
    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    return [String(parsed)];
  } catch {
    return [action];
  }
};

const mapSessionToCallRecord = (session: ChatSessionSummaryDto): CallRecord => {
  return {
    id: session.id,
    caller: session.caller || "Unknown Caller",
    phone: session.userId || "Unknown",
    timestamp: new Date(session.createdAt).toLocaleString(),
    summary: session.summary || "No summary available",
    sentiment: session.sentiment || "Neutral",
    duration: formatDuration(session.duration || 0),
    transcript: "", // Will be loaded on expand
    fullSummary: session.summary || "",
    reason: session.reason || "General Inquiry",
    actions: parseActions(session.action),
    messages: []
  };
};

export default function Dashboard() {
  const { resolveProjectId } = useProjectSelection();
  const [timeRange, setTimeRange] = useState("week");
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [recentCalls, setRecentCalls] = useState<CallRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});
  
  const [stats, setStats] = useState([
    { label: "Total Calls", value: "0", bg: "bg-stat-yellow" },
    { label: "Avg. Call", value: "0m 0s", bg: "bg-stat-mint" },
    { label: "Bookings", value: "0", bg: "bg-stat-purple" },
    { label: "Minutes", value: "0", bg: "bg-stat-peach" },
  ]);

  const [chartData, setChartData] = useState<{ day: string; calls: number }[]>([]);
  const [hourlyData, setHourlyData] = useState<{ hour: string; calls: number }[]>([]);

  const calculateStats = (sessions: ChatSessionSummaryDto[]) => {
    const totalCalls = sessions.length;
    
    // Calculate total duration in seconds
    const totalDuration = sessions.reduce((acc, curr) => acc + (curr.duration || 0), 0);
    const avgDuration = totalCalls > 0 ? Math.round(totalDuration / totalCalls) : 0;
    
    // Format duration
    const avgMinutes = Math.floor(avgDuration / 60);
    const avgSeconds = avgDuration % 60;
    const avgCallStr = `${avgMinutes}m ${avgSeconds}s`;

    // Bookings - prefer explicit booking flag, fall back to keyword detection if absent
    const hasBookingFlag = sessions.some(s => typeof s.bookingDone !== "undefined");
    const bookings = hasBookingFlag
      ? sessions.filter(s => s.bookingDone === true).length
      : sessions.filter(s => {
          const reason = (s.reason || "").toLowerCase();
          const action = (s.action || "").toLowerCase();
          return reason.includes("book") || reason.includes("appoint") ||
            action.includes("book") || action.includes("appoint");
        }).length;

    // Total Minutes
    const totalMinutes = Math.round(totalDuration / 60).toLocaleString();

    setStats([
      { label: "Total Calls", value: totalCalls.toString(), bg: "bg-stat-yellow" },
      { label: "Avg. Call", value: avgCallStr, bg: "bg-stat-mint" },
      { label: "Bookings", value: bookings.toString(), bg: "bg-stat-purple" },
      { label: "Minutes", value: totalMinutes, bg: "bg-stat-peach" },
    ]);
  };

  const processChartData = (sessions: ChatSessionSummaryDto[]) => {
    // Group by day
    const callsByDay: Record<string, number> = {};
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    
    // Initialize days depending on range? For now, we'll just track all days found or last 7 days for "week"
    // To make the chart look nice, we should probably fill in 0s for missing days if it's "week"
    
    // Basic grouping first
    sessions.forEach(s => {
      const date = new Date(s.createdAt);
      const day = days[date.getDay()];
      callsByDay[day] = (callsByDay[day] || 0) + 1;
    });

    // For "week", we might want to ensure all days of the week are present?
    // Or just map the data we have. Let's map what we have but sort by day index if possible,
    // or just rely on what we get.
    // Better: Sort by date.
    
    // Actually, AreaChart usually expects chronological order.
    // Let's create a map of DateString -> Count
    const callsByDate: Record<string, number> = {};
    sessions.forEach(s => {
      const date = new Date(s.createdAt).toLocaleDateString();
      callsByDate[date] = (callsByDate[date] || 0) + 1;
    });

    // For simplicity in this demo, let's stick to Day names as in the original mock if possible, 
    // but chronological is better.
    // If we use "Mon", "Tue" etc., we need to know WHICH Monday if it spans multiple weeks.
    // The original mock used "Mon", "Tue"... implying a single week view.
    // For larger ranges, we might want "Nov 1", "Nov 2".
    
    // Let's just group by formatted date
    const grouped = Object.entries(callsByDate).map(([dateStr, count]) => {
        const date = new Date(dateStr);
        return {
            dateObj: date,
            day: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            calls: count
        };
    }).sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

    setChartData(grouped.map(g => ({ day: g.day, calls: g.calls })));


    // Hourly Distribution
    const hours = Array.from({ length: 24 }, () => 0);
    // Initialize 0 for all hours? Or just show active ones. 
    // The mock had "8am" to "7pm".
    const formatHour = (h: number) => {
        const ampm = h >= 12 ? 'pm' : 'am';
        const hour12 = h % 12 || 12;
        return `${hour12}${ampm}`;
    };

    sessions.forEach(s => {
        const date = new Date(s.createdAt);
        const hour = date.getHours();
        hours[hour] += 1;
    });

    const hourlyChart = hours.map((count, hour) => ({
        hour: formatHour(hour),
        calls: count
    }));

    setHourlyData(hourlyChart);
  };

  const loadSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const now = new Date();
      let startDate: Date;
      
      switch (timeRange) {
        case "month":
          startDate = new Date(now.setDate(now.getDate() - 30));
          break;
        case "6months":
          startDate = new Date(now.setMonth(now.getMonth() - 6));
          break;
        case "week":
        default:
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
      }

      // Fetch ALL sessions for stats (high limit)
      const projectId = resolveProjectId();
      const allSessions = await chatbotService.listSessions({ 
        limit: 1000,
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString(),
        projectId
      });

      calculateStats(allSessions);
      processChartData(allSessions);

      // Set recent calls (first 5)
      const recent = allSessions.slice(0, 5).map(mapSessionToCallRecord);
      setCalls(recent); // Used for the list
      
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, [resolveProjectId, timeRange]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleExpand = async (id: string, isOpen: boolean) => {
    setExpandedCallId(isOpen ? id : null);
    
    if (isOpen) {
      const call = calls.find(c => c.id === id);
      if (call && (!call.messages || call.messages.length === 0) && !call.transcript) {
        await loadSessionDetails(id);
      }
    }
  };

  const loadSessionDetails = async (id: string) => {
    setLoadingDetails(prev => ({ ...prev, [id]: true }));
    try {
      const details = await chatbotService.getSession(id);
      setCalls(prevCalls => prevCalls.map(call => {
        if (call.id === id) {
          const transcript = details.messages
            ? details.messages.map(m => `${m.role}: ${m.content}`).join('\n\n')
            : "No transcript available";
            
          return {
            ...call,
            transcript,
            messages: details.messages,
            summary: details.summary || call.summary,
            fullSummary: details.summary || call.fullSummary,
            sentiment: details.sentiment || call.sentiment,
            reason: details.reason || call.reason,
            actions: details.action ? parseActions(details.action) : call.actions
          };
        }
        return call;
      }));
    } catch (error) {
      console.error(`Failed to load details for session ${id}:`, error);
      toast.error("Failed to load call details");
    } finally {
      setLoadingDetails(prev => ({ ...prev, [id]: false }));
    }
  };

  const getSentimentColor = (sentiment: string) => {
    const normalized = sentiment.toLowerCase();
    if (normalized.includes("positive")) return "bg-green-500/10 text-green-700 border-green-500/20";
    if (normalized.includes("negative")) return "bg-red-500/10 text-red-700 border-red-500/20";
    if (normalized.includes("neutral")) return "bg-blue-500/10 text-blue-700 border-blue-500/20";
    return "bg-secondary text-secondary-foreground";
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex justify-end">
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Last Week</SelectItem>
            <SelectItem value="month">Last Month</SelectItem>
            <SelectItem value="6months">Last 6 Months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className={`${stat.bg} stat-card border-0 cursor-pointer`}>
            <div className="space-y-2">
              <p className="text-sm text-foreground/70">{stat.label}</p>
              <p className="text-3xl lg:text-4xl font-semibold">{stat.value}</p>
            </div>
          </Card>
        ))}
      </div>

      <Card className="lg:hidden stat-card bg-stat-blue border-0 p-4">
        <p className="text-sm text-foreground/70 mb-2">Calls trend</p>
        <div className="h-16">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(262, 90%, 54%)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(262, 90%, 54%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="calls" stroke="hsl(262, 90%, 54%)" fill="url(#trendGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="hidden lg:grid lg:grid-cols-2 gap-6">
        <Card className="p-6 card-hover">
          <h3 className="text-lg font-semibold mb-4">Call Trends</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(262, 90%, 54%)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(262, 90%, 54%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                <Area type="monotone" dataKey="calls" stroke="hsl(262, 90%, 54%)" fill="url(#colorCalls)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 card-hover">
          <h3 className="text-lg font-semibold mb-4">Call Distribution by Hour</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <XAxis dataKey="hour" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                <Bar dataKey="calls" fill="hsl(262, 90%, 54%)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="card-hover">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Recent Calls</h3>
          <p className="text-sm text-muted-foreground">Latest customer interactions</p>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-4 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading recent calls...</p>
          </div>
        ) : calls.length === 0 ? (
          <div className="p-12 text-center">
            <Phone className="w-8 h-8 mx-auto mb-4 text-muted-foreground opacity-20" />
            <p className="text-muted-foreground">No recent calls found</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold w-[50px]"></th>
                    <th className="text-left p-4 font-semibold">Caller</th>
                    <th className="text-left p-4 font-semibold">Timestamp</th>
                    <th className="text-left p-4 font-semibold">Summary</th>
                    <th className="text-left p-4 font-semibold">Sentiment</th>
                    <th className="text-left p-4 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <Collapsible 
                      key={call.id} 
                      open={expandedCallId === call.id} 
                      onOpenChange={(open) => handleExpand(call.id, open)} 
                      asChild
                    >
                      <>
                        <CollapsibleTrigger asChild>
                          <tr className="border-b hover:bg-accent/50 cursor-pointer transition-colors">
                            <td className="p-4">
                              {expandedCallId === call.id ? (
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              )}
                            </td>
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{call.caller}</p>
                                <p className="text-sm text-muted-foreground">{call.phone}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">
                                <p>{call.timestamp.split(',')[0]}</p>
                                <p className="text-muted-foreground">{call.timestamp.split(',')[1]}</p>
                              </div>
                            </td>
                            <td className="p-4"><p className="text-sm line-clamp-2">{call.summary}</p></td>
                            <td className="p-4"><Badge className={getSentimentColor(call.sentiment)}>{call.sentiment}</Badge></td>
                            <td className="p-4 text-sm">{call.duration}</td>
                          </tr>
                        </CollapsibleTrigger>
                        <CollapsibleContent asChild>
                          <tr>
                            <td colSpan={6} className="p-0">
                              <div className="bg-muted/30 p-6 space-y-6">
                                {loadingDetails[call.id] ? (
                                  <div className="flex justify-center p-4">
                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                                        Full Transcript
                                        <Badge variant="secondary" className="text-xs">{call.duration}</Badge>
                                      </h4>
                                      <div className="bg-background rounded-lg p-4 max-h-[400px] overflow-y-auto">
                                        <pre className="text-sm whitespace-pre-wrap font-sans">{call.transcript || "No transcript available."}</pre>
                                      </div>
                                    </div>
                                    <div className="space-y-4">
                                      <div>
                                        <h4 className="font-semibold mb-2">Call Summary</h4>
                                        <p className="text-sm text-muted-foreground bg-background rounded-lg p-4">
                                          {call.fullSummary || "No summary available."}
                                        </p>
                                      </div>
                                      <div>
                                        <h4 className="font-semibold mb-2">Reason Detected</h4>
                                        <Badge variant="secondary" className="text-sm">
                                          {call.reason}
                                        </Badge>
                                      </div>
                                      <div>
                                        <h4 className="font-semibold mb-2">Actions Taken</h4>
                                        {call.actions.length > 0 ? (
                                          <ul className="space-y-2">
                                            {call.actions.map((action, actionIdx) => (
                                              <li key={actionIdx} className="flex items-start gap-2 text-sm">
                                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                                <span>{action}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-sm text-muted-foreground">No actions recorded.</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden p-4 space-y-4">
              {calls.map((call) => (
                <Collapsible 
                  key={call.id} 
                  open={expandedCallId === call.id} 
                  onOpenChange={(open) => handleExpand(call.id, open)}
                >
                  <Card className="border">
                    <CollapsibleTrigger className="w-full">
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 text-left">
                            <div className="font-semibold">{call.caller}</div>
                            <div className="text-sm text-muted-foreground">{call.phone}</div>
                          </div>
                          {expandedCallId === call.id ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-2" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-2" />
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground text-left">{call.timestamp}</div>
                        <div className="text-sm text-left">{call.summary}</div>
                        <div className="flex items-center justify-between">
                          <Badge className={getSentimentColor(call.sentiment)}>{call.sentiment}</Badge>
                          <span className="text-sm text-muted-foreground">Duration: {call.duration}</span>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t p-4 bg-muted/30 space-y-4">
                        {loadingDetails[call.id] ? (
                          <div className="flex justify-center p-4">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                          </div>
                        ) : (
                          <>
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Full Transcript</h4>
                              <div className="bg-background rounded-lg p-3 max-h-[300px] overflow-y-auto">
                                <pre className="text-xs whitespace-pre-wrap font-sans">{call.transcript || "No transcript available."}</pre>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Call Summary</h4>
                              <p className="text-sm text-muted-foreground">{call.fullSummary}</p>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Reason</h4>
                              <Badge variant="secondary">{call.reason}</Badge>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Actions Taken</h4>
                              {call.actions.length > 0 ? (
                                <ul className="space-y-1.5">
                                  {call.actions.map((action, actionIdx) => (
                                    <li key={actionIdx} className="flex items-start gap-2 text-sm">
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                      <span>{action}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-muted-foreground">No actions recorded.</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
